import React, {useState} from 'react';
import {
    TextField,
    Button,
    Paper,
    Typography,
    Stack,
    Box, Autocomplete,
} from '@mui/material';
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useNotification} from "../../utils/hooks/useNotification";
import {getFetcher, postFetcher} from "../../utils/api/fetchers";
import {useNavigate} from "react-router-dom";
import {BoatCreateRequest, boatCreateSchema} from "../../schemas/boats/boatCreateSchema";
import {Boat} from "../../schemas/boats/boatsSchema";
import {ColorInput} from "@mantine/core";
import {Category} from "../../schemas/category/categoriesSchema";
import {Club} from "../../schemas/club/clubSchema";
import useSWR from "swr";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

interface BoatCreateProps {
    mode?: 'dialog' | 'page';
    onSuccess?: (boat: Boat) => void;
    onCancel?: () => void;
}

const BoatCreate: React.FC<BoatCreateProps> = ({mode = "page", onSuccess, onCancel}) => {
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const {data: categories} = useSWR<Category[]>('/categories', getFetcher);
    const {data: clubs} = useSWR<Club[]>('/clubs/all', getFetcher);
    const {register, handleSubmit, setValue, formState: {errors}} = useForm<BoatCreateRequest>({
        resolver: zodResolver(boatCreateSchema),
    });
    const isMobile = useMediaQuery('(max-width: 600px)');

    const [colorValue, setColorValue] = useState<string | undefined>(undefined);
    const [colorNote, setColorNote] = useState<string | undefined>(undefined);

    const handleColorChange = (color: string) => {
        setColorValue(color);
        setValue('color', `${color}-${colorNote}`);
    };

    const handleNoteChange = (colorText: string) => {
        setColorNote(colorText);
        setValue('color', `${colorValue}-${colorText}`);
    };

    const onSubmit = async (data: BoatCreateRequest) => {
        postFetcher<BoatCreateRequest, Boat>("/boats", data)
            .then((res) => {
                showNotification("Loď byla úspěšně vytvořena", "success");
                if (mode === 'dialog') {
                    onSuccess?.(res);
                    return;
                }
                navigate('/boats');
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    showNotification("Loď s tímto startovním číslem již existuje", "error");
                } else {
                    showNotification("Nepodařilo se vytvořit loď", "error");
                }
            });
    };

    return (
        <Paper elevation={3} sx={{p: 4, maxWidth: 600, margin: '0 auto'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="h4" gutterBottom>
                    Vytvoření lodi
                </Typography>
                {mode === 'dialog' && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={onCancel}
                        startIcon={<HighlightOffIcon/>}
                    >
                        Zavřít
                    </Button>
                )}
            </Stack>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Jméno"
                        variant="standard"
                        required
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />
                    <Autocomplete
                        {...register('categoryId')}
                        options={categories || []}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => setValue('categoryId', value?.id ?? 0)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Kategorie"
                                variant="standard"
                                required
                                error={!!errors.categoryId}
                                helperText={errors.categoryId?.message}
                            />
                        )}
                    />
                    <Autocomplete
                        {...register('clubId', {
                            setValueAs: v => v === "" ? undefined : Number(v),
                        })}
                        options={clubs || []}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => setValue('clubId', value?.id ?? undefined)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Klub"
                                variant="standard"
                                error={!!errors.clubId}
                                helperText={errors.clubId?.message}
                            />
                        )}
                    />
                    <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                        <TextField
                            fullWidth
                            label="Evidenční číslo"
                            variant="standard"
                            {...register('boatNumber')}
                            error={!!errors.boatNumber}
                            helperText={errors.boatNumber?.message}
                        />
                        <TextField
                            fullWidth
                            label="Číslo na plachtě"
                            variant="standard"
                            {...register('sailNumber')}
                            error={!!errors.sailNumber}
                            helperText={errors.sailNumber?.message}
                        />
                        <TextField
                            fullWidth
                            label="Závodní číslo (trvalé)"
                            variant="standard"
                            type="number"
                            {...register('raceNumber', {
                                setValueAs: v => v === "" ? undefined : Number(v),
                            })}
                            error={!!errors.raceNumber}
                            helperText={errors.raceNumber?.message}
                            inputProps={{min: 0}}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Typ lodi"
                            variant="standard"
                            {...register('type')}
                            error={!!errors.type}
                            helperText={errors.type?.message}
                        />
                        <TextField
                            fullWidth
                            label="Délka lodi"
                            variant="standard"
                            {...register('length', {
                                setValueAs: (v: string) => {
                                    if (!v) return undefined;
                                    const cleaned = v.replace(',', '.').replace(/[^\d.]/g, '');
                                    const parsed = parseFloat(cleaned);
                                    return isNaN(parsed) ? undefined : parsed;
                                },
                                validate: (value) =>
                                    typeof value === 'number' && !isNaN(value) && value >= 0
                                        ? true
                                        : 'Zadejte platnou délku v metrech',
                            })}
                            error={!!errors.length}
                            helperText={errors.length?.message}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">m</InputAdornment>,
                            }}
                            inputProps={{
                                inputMode: "decimal",
                                pattern: "[0-9]+([.,][0-9]+)?",
                                onKeyDown: (e) => {
                                    const allowedKeys = [
                                        'Backspace',
                                        'Tab',
                                        'Delete',
                                        'ArrowLeft',
                                        'ArrowRight',
                                        'Home',
                                        'End',
                                        'Enter',
                                    ];
                                    const isNumber = /^[0-9.,]$/.test(e.key);
                                    if (!isNumber && !allowedKeys.includes(e.key)) {
                                        e.preventDefault();
                                    }
                                },
                            }}
                        />
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={2}>
                        <TextField
                            label="Barva"
                            variant="standard"
                            fullWidth
                            value={colorNote}
                            onChange={(event) => handleNoteChange(event.target.value)}
                            error={!!errors.color}
                            helperText={errors.color?.message}
                        />
                        <ColorInput
                            title="Vyberte barvu"
                            value={colorValue}
                            onChange={(event) => handleColorChange(event)}
                            error={!!errors.color}
                            swatchesPerRow={5}
                            swatches={[
                                '#000000',
                                '#FFFFFF',
                                '#FF0000',
                                '#008000',
                                '#0000FF',
                                '#ffa915',
                                '#FFFF00',
                                '#808080',
                                '#00008B',
                                '#800020',
                            ]}
                        />
                    </Stack>

                    <Button type="submit" variant="contained" color="primary" fullWidth>
                        Vytvořit loď
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
};

export default BoatCreate;